import React from 'react';
import { Autocomplete, InputAdornment, PopperProps, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import {InputField} from '@/pages/components/common/InputField';

export interface AutocompleteFieldProps {
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
  label?: React.ReactNode;
  /** Hover label tip content (forwarded to InputField). */
  popoverContent?: React.ReactNode;
  placeholder?: string;
  size?: 'small' | 'medium';
  disablePortal?: boolean;
  slotProps?: { popper?: PopperProps | any } | any;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  align?: 'left' | 'center' | 'right'; // text alignment (default: right)
  /** freeSolo 模式下 blur 时提交当前输入值 */
  commitOnBlur?: boolean;
  /** 替换默认可清除图标，在输入框内右侧展示静态后缀（不可点击） */
  endSuffix?: string;
  /** freeSolo 输入允许的小数位数上限，设置后对输入进行校验与截断 */
  decimalPlaces?: number;
  /** Keep a single-line input; do not expand to multiline when the value overflows. */
  singleLine?: boolean;
  /**
   * Show the popup indicator. MUI hides it by default when freeSolo is on
   * (forcePopupIcon='auto'); pass true for select-like bank / dictionary pickers.
   */
  forcePopupIcon?: boolean | 'auto';
  /**
   * When true, each listbox option stays on one line and the popup grows to show
   * the full option text (not clipped to the input width).
   */
  fullWidthOptions?: boolean;
  /**
   * When set with singleLine, force input width to this many `ch` units
   * (e.g. widest row / option across a column) instead of the current value length.
   */
  singleLineWidthCh?: number;
  /** Subtract from the singleLine `ch` width (Bank WP: 15px tighter than measured text). */
  singleLineOffsetPx?: number;
  /**
   * With singleLine, fit the input to its container (fill the available width) instead of
   * locking the width to the value length / singleLineWidthCh. Used when the dialog/paper
   * itself adapts to the widest content and the input must stay inside it.
   */
  singleLineFit?: boolean;
  /**
   * Allow typing a value that is not in options. Default true (existing callers).
   * Set false for dictionary pickers that must select from the list.
   */
  freeSolo?: boolean;
  /**
   * Hover hint for a list option (e.g. AI recommendation reason).
   */
  getOptionTooltip?: (option: string) => string | undefined;
  /**
   * Fires when the input text changes (for remote search / AI recommend).
   */
  onInputChange?: (inputValue: string, reason: string) => void;
  /**
   * Show a loading indicator in the popup while options are fetched.
   */
  loading?: boolean;
  /**
   * Disable the control (read-only WP).
   */
  disabled?: boolean;
  /**
   * Called when the input is focused (e.g. load dictionary options).
   */
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  /**
   * Open the popup when the field receives focus. Default false.
   */
  openOnFocus?: boolean;
  /**
   * When true, skip client-side option filtering (remote / full-list modes).
   * Default false so existing pickers keep MUI typeahead filtering.
   */
  disableClientFilter?: boolean;
}

function sanitizeDecimalInput(raw: string, maxFraction: number): string {
  let s = raw.replace(/,/g, '').trim().replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot >= 0) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  const [intPart = '', decPart] = s.split('.') as [string, string | undefined];
  if (firstDot >= 0) {
    const trimmedDec = decPart?.slice(0, maxFraction) ?? '';
    return `${intPart}.${trimmedDec}`;
  }
  return intPart;
}

/**
 * CustomAutocomplete
 * - Based on MUI Autocomplete with styles aligned to InputField
 * - After user selects/types a value, if the display text would overflow the input area,
 *   switches to render a big-field Select using InputField (for better readability)
 */
export function AutocompleteField({
  options,
  value,
  onChange,
  label,
  popoverContent,
  placeholder,
  size = 'small',
  disablePortal = false,
  slotProps,
  sx,
  fullWidth,
  align = 'right',
  commitOnBlur = false,
  endSuffix,
  decimalPlaces,
  singleLine = false,
  forcePopupIcon = 'auto',
  fullWidthOptions = false,
  singleLineWidthCh,
  singleLineOffsetPx,
  singleLineFit = false,
  freeSolo = true,
  getOptionTooltip,
  onInputChange,
  loading = false,
  disabled = false,
  onFocus,
  openOnFocus = false,
  disableClientFilter = false,
}: AutocompleteFieldProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [overflow, setOverflow] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  // Sync inputValue when value changes from outside
  React.useEffect(() => {
    if (value === null) {
      setInputValue('');
    } else if (value !== inputValue) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Measure whether current value would overflow the input display area.
  // Once overflow flips to multiline, do not remeasure against the multiline
  // box (different width) or Autocomplete ↔ InputField will bounce forever.
  const measureOverflow = React.useCallback(() => {
    if (singleLine) return;
    try {
      const el = inputRef.current;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      const font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.font = font;
      const text = String(value ?? '');
      const metrics = ctx.measureText(text);
      const contentWidth = el.clientWidth - 16;
      const next = metrics.width > contentWidth;
      setOverflow((prev) => (prev === next ? prev : next));
    } catch {
      // keep previous overflow
    }
  }, [value, singleLine]);

  React.useEffect(() => {
    if (singleLine) {
      setOverflow(false);
      return;
    }
    // Remeasure only while still on Autocomplete; stay multiline until value changes.
    setOverflow(false);
  }, [value, singleLine]);

  React.useEffect(() => {
    if (singleLine || overflow) return;
    const id = requestAnimationFrame(() => measureOverflow());
    return () => cancelAnimationFrame(id);
  }, [measureOverflow, value, singleLine, overflow]);

  React.useEffect(() => {
    if (singleLine || overflow) return;
    const onResize = () => measureOverflow();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureOverflow, singleLine, overflow]);

  // Must stay above any early return — overflow mode used to skip this and break hook order
  const mergedSlotProps = React.useMemo(() => {
    if (!fullWidthOptions) return slotProps as any;
    const base = (slotProps || {}) as Record<string, any>;
    const basePopper = base.popper || {};
    const basePaper = base.paper || {};
    const baseListbox = base.listbox || {};
    return {
      ...base,
      popper: {
        ...basePopper,
        style: {width: 'auto', ...(basePopper.style || {})},
        sx: [
          {
            width: 'fit-content !important',
            minWidth: 220,
            maxWidth: 'none',
          },
          basePopper.sx,
        ].filter(Boolean),
      },
      paper: {
        ...basePaper,
        sx: [
          {
            width: 'fit-content',
            minWidth: 220,
            maxWidth: 'none',
          },
          basePaper.sx,
        ].filter(Boolean),
      },
      listbox: {
        ...baseListbox,
        sx: [
          {
            width: 'fit-content',
            minWidth: 220,
            maxWidth: 'none',
            boxSizing: 'border-box',
            '& .MuiAutocomplete-option': {
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
              display: 'block',
              lineHeight: 1.5,
              width: 'max-content',
              minWidth: '100%',
              boxSizing: 'border-box',
            },
          },
          baseListbox.sx,
        ].filter(Boolean),
      },
    };
  }, [fullWidthOptions, slotProps]);

  // InputField 高度对齐：消除 Autocomplete inputRoot 额外垂直内边距
  const inputFieldHeightSx = {
    position: 'relative',
    lineHeight: 1.2,
    '& .MuiInputBase-root': {
      height: 'auto',
      minHeight: 'auto',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      right: 2,
      top: '50%',
      transform: 'translateY(-50%)',
      height: 'auto',
      maxHeight: '100%',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 28,
    },
  } as const;

  // When in overflow mode, render as a big-field Select using InputField to align styles
  if (!singleLine && overflow && value) {
    return (
      <InputField
        align={align}
        label={label}
        popoverContent={popoverContent}
        multiline
        minRows={3}
        maxRows={8}
        size={size}
        value={value}
        onChange={(e: any) => onChange(e.target.value || '')}
        placeholder={placeholder}
        sx={{
          ...(sx as any),
          '& .MuiInputBase-input': {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          },
        }}
        fullWidth={fullWidth}
      />
    );
  }

  const suffixAdornment = endSuffix ? (
    <InputAdornment position="end" sx={{pointerEvents: 'none', mr: 0.25}}>
      <Typography variant="body2" color="text.secondary">{endSuffix}</Typography>
    </InputAdornment>
  ) : null;

  const singleLineLen = Math.max(
    12,
    typeof singleLineWidthCh === 'number' && singleLineWidthCh > 0
      ? Math.ceil(singleLineWidthCh)
      : String(inputValue || value || placeholder || '').length + 4,
  );
  const singleLineWidth = (singleLineOffsetPx ?? 0) > 0
    ? `calc(${singleLineLen}ch - ${singleLineOffsetPx}px)`
    : `${singleLineLen}ch`;
  const singleLineInputSx = singleLine ? {
    width: singleLineFit ? '100%' : 'max-content',
    minWidth: 110,
    '& .MuiInputBase-root': {
      width: singleLineFit ? '100%' : 'max-content',
    },
    '& .MuiAutocomplete-input, & .MuiInputBase-input': singleLineFit ? {
      width: '100%',
      minWidth: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'clip',
    } : {
      width: `${singleLineWidth} !important`,
      minWidth: `${singleLineWidth} !important`,
      flexGrow: 0,
      whiteSpace: 'nowrap',
      textOverflow: 'clip',
    },
  } : {};

  return (
    <Autocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        // Only accept a list option when freeSolo is off.
        if (!freeSolo && newValue != null && typeof newValue === 'string' && !options.includes(newValue)) {
          return;
        }
        onChange(typeof newValue === 'string' ? newValue : null);
      }}
      onInputChange={(_, newInputValue, reason) => {
        const nextValue = decimalPlaces != null && reason === 'input'
          ? sanitizeDecimalInput(newInputValue, Math.max(0, decimalPlaces))
          : newInputValue;
        setInputValue(nextValue);
        if (reason === 'clear') {
          onChange(null);
        }
        onInputChange?.(nextValue, reason);
      }}
      freeSolo={freeSolo}
      forcePopupIcon={forcePopupIcon}
      loading={loading}
      disabled={disabled}
      openOnFocus={openOnFocus}
      onFocus={onFocus}
      size={size}
      disablePortal={disablePortal}
      disableClearable={!!endSuffix}
      slotProps={mergedSlotProps}
      filterOptions={disableClientFilter ? ((opts) => opts) : undefined}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
        const tip = getOptionTooltip?.(option);
        const row = (
          <li
            key={key}
            {...optionProps}
            style={{
              ...(optionProps.style || {}),
              ...(fullWidthOptions ? { whiteSpace: 'nowrap' } : {}),
            }}
          >
            {option}
          </li>
        );
        if (!tip) {
          return row;
        }
        return (
          <Tooltip
            key={key}
            title={tip}
            placement="right"
            enterDelay={200}
            describeChild
            slotProps={{
              tooltip: {
                // One size above MUI Tooltip default (~0.6875rem) → body2 (0.875rem).
                sx: {fontSize: (theme) => theme.typography.body2.fontSize},
              },
            }}
          >
            {row}
          </Tooltip>
        );
      }}
      sx={{
        minWidth: 110,
        ...inputFieldHeightSx,
        ...singleLineInputSx,
        ...(sx as any),
      }}
      renderInput={(params) => (
        <InputField
          {...params}
          compact
          align={align}
          label={label}
          popoverContent={popoverContent}
          placeholder={placeholder}
          size={size}
          inputRef={inputRef}
          sx={{...inputFieldHeightSx, ...singleLineInputSx}}
          inputProps={{
            ...params.inputProps,
            ...(singleLine ? {size: singleLineLen} : {}),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: suffixAdornment ?? params.InputProps.endAdornment,
          }}
          onBlur={(event) => {
            params.inputProps?.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
            // freeSolo off: never commit typed text that is not an option.
            if (!freeSolo || !commitOnBlur || inputValue.trim() === '') {
              return;
            }
            const committed = decimalPlaces != null
              ? sanitizeDecimalInput(inputValue.trim(), Math.max(0, decimalPlaces))
              : inputValue.trim();
            onChange(committed);
          }}
        />
      )}
      fullWidth={fullWidth}
    />
  );
};
